import styled from 'styled-components';
import { Card, FlexCenter, FlexMd, Headline5 } from '../../../components/Elements';
import { breakpoints, sizes, theme } from '../../../theme';
import { LearnerActivity } from '../../../models/LearnerCourse';
import NotificationIcon from '../../../assets/icons/navigation/NotificationIcon';
import strings from '../../../strings/strings.json';
import { IconSize } from '../../../assets/icons/icon-sizes';
import ArrowRightIcon from '../../../assets/icons/controls/ArrowRightIcon';
import { getFriendlyTimeString } from '../../../lib/time-helpers';
import React from 'react';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import Button from 'components/buttons/Button';

export default function NextLiveTrainingMeetingCard({ nextActivity }: { nextActivity: LearnerActivity }): JSX.Element {
    const { setStep } = React.useContext(LearnerCourseContext);

    React.useEffect(() => {
        function calculateMinutesTilStart() {
            const newMinutes = Math.floor(
                (new Date(nextActivity.startDate ?? 0).getTime() - new Date().getTime()) / (1000 * 60),
            );
            setMinutesTilStart(newMinutes);
        }

        calculateMinutesTilStart();
        const timer = setInterval(() => calculateMinutesTilStart(), 5000);

        return () => clearInterval(timer);
    }, []); // eslint-disable-line

    const [minutesTilStart, setMinutesTilStart] = React.useState<number | null>(null);

    if (!minutesTilStart) {
        return (
            <NextLiveTrainingCardContainer>
                <NotificationIconContainer>
                    <NotificationIcon colour={theme.textColourInverted} size={IconSize.LARGE} />
                </NotificationIconContainer>
                <Headline5>{strings.learnerCoursePage.nextLiveTrainingMeeting.startingSoon}</Headline5>
            </NextLiveTrainingCardContainer>
        );
    }

    const minutesTilEnd = minutesTilStart + (nextActivity.durationInMins ?? 0);

    return (
        <NextLiveTrainingCardContainer>
            <NotificationIconContainer>
                <NotificationIcon colour={theme.textColourInverted} size={IconSize.LARGE} />
            </NotificationIconContainer>
            <TimeTextLine>
                {minutesTilEnd > 0 ? (
                    <>
                        <Headline5>
                            {nextActivity.title}{' '}
                            {minutesTilStart > 0
                                ? strings.learnerCoursePage.nextLiveTrainingMeeting.willStartInText
                                : strings.learnerCoursePage.nextLiveTrainingMeeting.startedText}
                        </Headline5>
                        <TimeContainer>
                            <Headline5>
                                {minutesTilStart > 0
                                    ? minutesTilStart === 1
                                        ? minutesTilStart.toString() +
                                          '\u00A0' +
                                          strings.learnerCoursePage.nextLiveTrainingMeeting.minutesSingular
                                        : minutesTilStart.toString() +
                                          '\u00A0' +
                                          strings.learnerCoursePage.nextLiveTrainingMeeting.minutes
                                    : getFriendlyTimeString(new Date(nextActivity.startDate ?? 0))}
                                .
                            </Headline5>
                        </TimeContainer>
                    </>
                ) : (
                    <Headline5>
                        {nextActivity.title} {strings.learnerCoursePage.nextLiveTrainingMeeting.hasFinishedText}
                    </Headline5>
                )}
                <Button
                    title={strings.learnerCoursePage.nextLiveTrainingMeeting.joinButton.title}
                    aria={strings.learnerCoursePage.nextLiveTrainingMeeting.joinButton.aria}
                    icon={<ArrowRightIcon />}
                    iconOnRight
                    onClick={() => setStep({ id: nextActivity.activityStatusId, stepType: 'Activity' })}
                />
            </TimeTextLine>
        </NextLiveTrainingCardContainer>
    );
}

const TimeTextLine = styled(FlexMd)`
    align-items: center;
    flex-wrap: wrap;
`;

const TimeContainer = styled(FlexCenter)`
    background-color: ${theme.cardSecondaryBackgroundColour};
    border-radius: ${sizes.borderRadiusMd};
    height: 100%;
    padding: ${sizes.spacingMd};
    max-height: 68px;
`;

const NotificationIconContainer = styled(FlexCenter)`
    border-radius: 50%;
    height: 70px;
    min-width: 70px;
    background-color: ${theme.primaryBrandColour};
`;

const NextLiveTrainingCardContainer = styled(Card)`
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    gap: ${sizes.spacingMd};
    max-width: ${sizes.learnerCourseContentMaxWidth};

    @media (max-width: ${breakpoints.md}) {
        width: 100%;
    }
`;
